import React, { useContext, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import ErrorBoundary from 'components/Common/ErrorBoundary';
import Row from 'components/Common/Row';
import useRedirectIfNotStaff from 'hooks/useRedirectIfNotStaff';

import AccountKnowledgeProvider from './AccountKnowledgeProvider';
import AgentKbProvider, { AgentKbContext } from './AgentKbProvider';
import KbValidationProvider from './KbValidationProvider';
import KbArtefactPage from './KbArtefactPage';
import KnowledgeTab from './KnowledgeTab';
import SuggestionsTab from './SuggestionsTab';
import DomainKnowledgePanel from './DomainKnowledgePanel';
import DomainReportsPanel from './DomainReportsPanel';
import UnflaggedPanel from './UnflaggedPanel';
import VersionsDialog from './VersionsDialog';
import KbBrowserDialog from './KbBrowserDialog';
import { kbVersionLabel } from './kbVersionLabel';
import AgentEvalProvider from './eval/AgentEvalProvider';
import EvalPanel from './eval/EvalPanel';
import EvalSummaryPill from './eval/EvalSummaryPill';
import TryItDrawerProvider, {
  useTryItDrawer,
} from './tryIt/TryItDrawerProvider';
import {
  ChecksRedirect,
  ContentRedirect,
  IndexRoute,
  SuggestionsRedirect,
  TryItRedirect,
} from './LegacyRedirects';

// Agent Management (ENG-1059 / ENG-1064) — one domain-first tree, no tab bar.
// The left nav is built from data already loaded (so it costs nothing); each
// route mounts its own analysis gate only where the full picture is needed. Try
// it is a header-launched drawer; versioning is a header button → modal.

const ScreenDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--color-bg-primary);
`;

const BodyDiv = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const AgentManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    resolvedVersion,
    bucketLatestVersion,
    bucketVersions,
    versions,
    isLoading,
  } = useContext(AgentKbContext);
  const { openTryIt } = useTryItDrawer();
  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);
  const [isKbBrowserOpen, setIsKbBrowserOpen] = useState<boolean>(false);

  const versionLabel = isLoading
    ? 'Loading…'
    : kbVersionLabel({
        resolvedVersion,
        bucketLatestVersion,
        bucketVersions,
        versions,
      });

  // The /try-it legacy route redirects here with this flag — open the drawer
  // once and clear it so a refresh doesn't reopen it.
  const openTryItFlag = (location.state as { openTryIt?: boolean } | null)
    ?.openTryIt;
  useEffect(() => {
    if (openTryItFlag) {
      openTryIt();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTryItFlag]);

  return (
    <ScreenDiv data-testid="agent-management-screen">
      <Row centerAlign spaceBetween style={{ padding: '16px 24px 0 24px' }}>
        <Typography.Header type="H2">AI Agent</Typography.Header>
        <Row centerAlign style={{ gap: 8 }}>
          <EvalSummaryPill />
          <Button
            type="Secondary"
            size="Small"
            label="Try it"
            testId="agent-management-try-it-button"
            onClick={() => openTryIt()}
          />
          <Button
            type="Secondary"
            size="Small"
            icon="find-in-page"
            testId="agent-management-browse-kb-button"
            onClick={() => setIsKbBrowserOpen(true)}
          />
          <Button
            type="Secondary"
            size="Small"
            label={versionLabel}
            testId="agent-management-version-button"
            onClick={() => setIsVersionsOpen(true)}
          />
        </Row>
      </Row>
      <BodyDiv>
        <Routes>
          <Route index element={<IndexRoute />} />
          <Route path="kb/:artefactId" element={<KbArtefactPage />} />
          <Route path="domains/:domainId" element={<DomainKnowledgePanel />} />
          <Route
            path="domains/:domainId/reports"
            element={<DomainReportsPanel />}
          />
          {/* The landing page. EvalPanel mounts the analysis gate itself,
              around the detail pane only, so the list never waits. */}
          <Route path="checks" element={<EvalPanel />} />
          <Route path="checks/:checkId" element={<EvalPanel />} />
          <Route path="account" element={<KnowledgeTab />} />
          <Route path="account/suggestions" element={<SuggestionsTab />} />
          <Route path="unflagged" element={<UnflaggedPanel />} />
          {/* Legacy deep links. */}
          <Route path="content" element={<ContentRedirect />} />
          <Route path="tests" element={<ChecksRedirect />} />
          <Route path="eval" element={<ChecksRedirect />} />
          <Route path="domains/:domainId/tests" element={<ChecksRedirect />} />
          <Route path="suggestions" element={<SuggestionsRedirect />} />
          <Route path="try-it" element={<TryItRedirect />} />
        </Routes>
      </BodyDiv>
      {isVersionsOpen && (
        <VersionsDialog close={() => setIsVersionsOpen(false)} />
      )}
      {isKbBrowserOpen && (
        <KbBrowserDialog close={() => setIsKbBrowserOpen(false)} />
      )}
    </ScreenDiv>
  );
};

const Gate = () => {
  // The system knowledge base shapes every customer's agent — staff only, not
  // account admins. The server enforces the same rule independently.
  useRedirectIfNotStaff();

  return (
    <ErrorBoundary isRetryable>
      <AgentKbProvider>
        <AccountKnowledgeProvider>
          <AgentEvalProvider>
            <KbValidationProvider>
              <TryItDrawerProvider>
                <AgentManagement />
              </TryItDrawerProvider>
            </KbValidationProvider>
          </AgentEvalProvider>
        </AccountKnowledgeProvider>
      </AgentKbProvider>
    </ErrorBoundary>
  );
};

export default Gate;
